/**
 * Pure helpers for deciding what should happen at the boundary between the
 * spoken/code-question phase and the optional psychometric phase of an
 * interview.
 *
 * Kept dependency-free so the timer-expiry path and the manual-click path
 * can share a single source of truth. Previously these two paths drifted
 * apart and the click path silently ended the interview when a psychometric
 * section was attached (see Frontend developer / DISC incident, 2026-05).
 */

export type EndOfQuestionsAction = 'advance-to-psychometric' | 'end-interview';

export type EndOfQuestionsLabelKey =
  | 'live.actions.ending'
  | 'live.actions.continue_personality'
  | 'live.actions.end_submit';

/**
 * Decide what should happen when the candidate reaches the end of the
 * spoken/code questions — either by clicking the action button or because
 * the last question's timer expired.
 */
export function endOfQuestionsAction(
  psychometricSectionCount: number
): EndOfQuestionsAction {
  return psychometricSectionCount > 0
    ? 'advance-to-psychometric'
    : 'end-interview';
}

/**
 * i18n key for the action button at the end of the spoken/code phase.
 * The label MUST match the action: a label that promises personality test
 * must never be wired to an end-interview handler.
 */
export function endOfQuestionsButtonLabelKey(
  psychometricSectionCount: number,
  isEnding: boolean
): EndOfQuestionsLabelKey {
  if (isEnding) return 'live.actions.ending';
  return psychometricSectionCount > 0
    ? 'live.actions.continue_personality'
    : 'live.actions.end_submit';
}

/** @deprecated Use endOfQuestionsButtonLabelKey with i18n.t() */
export function endOfQuestionsButtonLabel(
  psychometricSectionCount: number,
  isEnding: boolean
): string {
  const key = endOfQuestionsButtonLabelKey(psychometricSectionCount, isEnding);
  const labels: Record<EndOfQuestionsLabelKey, string> = {
    'live.actions.ending': 'Ending...',
    'live.actions.continue_personality': 'Continue to Personality Test',
    'live.actions.end_submit': 'End & Submit Interview',
  };
  return labels[key];
}

/**
 * DOM id for the action button. Kept paired with the action so a future
 * regression that swaps the handler also makes the id mismatch obvious in
 * snapshots / Playwright traces.
 */
export function endOfQuestionsButtonId(
  psychometricSectionCount: number
): string {
  return psychometricSectionCount > 0
    ? 'advance-to-psychometric-btn'
    : 'end-interview-btn';
}
